import React, { useEffect, useMemo, useState } from 'react';
import { settingsAPI, tiktokAPI } from '../../services/api';

const extractVideoId = (url) => {
  if (!url) return '';
  const match = url.match(/video\/(\d+)/);
  return match ? match[1] : '';
};

const TikTokEmbed = ({ url }) => {
  if (!url) return null;
  
  // Support local uploads playback
  if (url.startsWith('/uploads/')) {
    return (
      <div className="w-full aspect-[9/16] bg-tech-surface rounded-lg overflow-hidden">
        <video src={url} controls className="w-full h-full object-contain" />
      </div>
    );
  }
  
  const videoId = extractVideoId(url);
  if (!videoId) return null;
  
  const src = `https://www.tiktok.com/embed/v2/${videoId}`;
  return (
    <div className="w-full aspect-[9/16] bg-tech-surface rounded-lg overflow-hidden">
      <iframe
        title={videoId}
        src={src}
        allow="encrypted-media; fullscreen; picture-in-picture"
        className="w-full h-full"
        frameBorder="0"
      />
    </div>
  );
};

const TikTokTab = ({ person, updateField }) => {
  const accounts = useMemo(() => person?.socialMedia?.tiktok || [], [person]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [newUrl, setNewUrl] = useState('');
  const [newUrlAccount, setNewUrlAccount] = useState('');
  const [cookies, setCookies] = useState('');
  const [savingCookies, setSavingCookies] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setSelectedAccount('all');
    setNewUrlAccount(accounts[0]?.username || '');
  }, [accounts]);

  const videos = person.tiktokVideos || [];
  const filteredVideos = (selectedAccount === 'all'
    ? videos
    : videos.filter(v => v.account === selectedAccount))
    .slice()
    .sort((a,b) => new Date(b.addedAt || 0) - new Date(a.addedAt || 0));

  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.getTikTokCookies();
        setCookies(res.data.cookies || '');
      } catch {}
    })();
  }, []);

  const saveCookies = async () => {
    setSavingCookies(true);
    try {
      await settingsAPI.setTikTokCookies(cookies);
    } finally {
      setSavingCookies(false);
    }
  };

  const addVideo = async () => {
    if (!newUrl) return;
    setDownloading(true);
    try {
      const res = await tiktokAPI.download(person.id, newUrl, newUrlAccount || selectedAccount || '');
      const entry = res.data.video;
      updateField('tiktokVideos', [...videos, entry]);
      setNewUrl('');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const removeVideo = async (id) => {
    try {
      await tiktokAPI.deleteVideo(person.id, id);
      updateField('tiktokVideos', videos.filter(v => v.id !== id));
    } catch (e) {
      alert('Failed to delete video');
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4 p-4">
      {/* Account Selection & Download Bar */}
      <div className="tech-card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs font-mono text-tech-textMuted">ACCOUNT:</label>
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              className="tech-input py-1.5"
            >
              <option value="all">ALL</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.username}>{acc.username}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 flex items-center gap-2 min-w-[300px]">
            <input
              type="url"
              placeholder="Paste TikTok URL..."
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              className="tech-input py-1.5"
            />
            <select
              value={newUrlAccount}
              onChange={(e) => setNewUrlAccount(e.target.value)}
              className="tech-input py-1.5"
            >
              <option value="">-- SELECT ACCOUNT --</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.username}>{acc.username}</option>
              ))}
            </select>
          </div>
          <button 
            onClick={addVideo} 
            disabled={downloading || !newUrl} 
            className="tech-btn-primary disabled:opacity-50"
          >
            {downloading ? 'DOWNLOADING...' : 'DOWNLOAD'}
          </button>
          <Settings cookies={cookies} setCookies={setCookies} saving={savingCookies} onSave={saveCookies} />
        </div>
      </div>

      {/* Videos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredVideos.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="text-tech-textDim text-sm">NO TIKTOK VIDEOS YET</p>
          </div>
        ) : (
          filteredVideos.map(v => (
            <div key={v.id} className="relative group">
              <TikTokEmbed url={v.url} />
              <button
                onClick={() => removeVideo(v.id)}
                className="absolute top-2 right-2 p-2 bg-tech-danger text-white rounded opacity-0 group-hover:opacity-100 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const Settings = ({ cookies, setCookies, saving, onSave }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="tech-btn-secondary py-1.5" title="Cookie Settings">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
      {open && (
        <div className="fixed inset-0 bg-tech-bg/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setOpen(false)}>
          <div className="tech-card max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-mono font-semibold text-tech-text mb-4">TIKTOK COOKIES</h3>
            <p className="text-tech-textMuted text-sm mb-3">msToken=...; tt_csrf_token=...; ttwid=...</p>
            <textarea 
              rows={5} 
              value={cookies} 
              onChange={(e)=>setCookies(e.target.value)} 
              className="tech-input font-mono text-sm mb-4"
              placeholder="msToken=...; tt_csrf_token=...; ttwid=..."
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="tech-btn-secondary">CLOSE</button>
              <button onClick={onSave} disabled={saving} className="tech-btn-primary disabled:opacity-50">
                {saving ? 'SAVING...' : 'SAVE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TikTokTab;
